/** W75 (D-287): Absender, Name, Mail und Dateiname eines Briefs. Die Regeln folgen dem Server (`briefpdf.py`,
 *  `schemas/konto.py`), damit Formular und PDF dasselbe sagen. */
import { ApiFehler, fehlerText } from "../../api/client";
import { ANSCHRIFT_FELDER, type Anschrift, type AnschriftFeld, type Brief, type Person } from "../../api/typen";
import { PLATZHALTER } from "./platzhalter";

export const LEERE_ANSCHRIFT: Anschrift = { vorname: "", nachname: "", strasse: "", plz: "", ort: "", telefon: "", email: "" };

/** Laengen wie der Server (`Feld60`, `Feld100`, `Postleitzahl`, `Telefon`, `Feld254`). */
export const LAENGE: Record<AnschriftFeld, number> = { vorname: 60, nachname: 60, strasse: 100, plz: 10, ort: 60, telefon: 40, email: 254 };

/** Beschriftungen im Blatt „Absender“ und in „Meine Anschrift“ (plan-w75.md Kapitel 6). */
export const BESCHRIFTUNG: Record<AnschriftFeld, string> = {
  vorname: "Vorname",
  nachname: "Nachname",
  strasse: "Straße und Hausnummer",
  plz: "PLZ",
  ort: "Ort",
  telefon: "Telefon (freiwillig)",
  email: "E-Mail (freiwillig)",
};

/** Alle sieben Felder als Zeichenketten; null, fehlend und nur Leerraum sind leer (H9: leer heisst ausgeschaltet). */
export function vollstaendig(roh: Partial<Record<AnschriftFeld, string | null | undefined>> | null | undefined): Anschrift {
  const a = { ...LEERE_ANSCHRIFT };
  for (const f of ANSCHRIFT_FELDER) a[f] = (roh?.[f] ?? "").trim();
  return a;
}

export function anschriftLeer(a: Partial<Record<AnschriftFeld, string | null | undefined>> | null | undefined): boolean {
  const v = vollstaendig(a);
  return ANSCHRIFT_FELDER.every((f) => !v[f]);
}

/** W82 Web-5c (K29, entscheide E7): Name aus Anmeldung oder Person für das erste Vorbelegen in Vor- und Nachname. Leerraum
 *  (auch mehrfach, Tab, Zeilenumbruch) zählt als eine Lücke; das letzte Wort ist der Nachname, der Rest der Vorname; ein
 *  einzelnes Wort bleibt Vorname. Dieselbe Regel gilt für iOS und Android (Abgleich Web-5). */
export function nameTeilen(name: string): { vorname: string; nachname: string } {
  const woerter = name.trim().split(/\s+/).filter(Boolean);
  if (woerter.length < 2) return { vorname: woerter[0] ?? "", nachname: "" };
  return { vorname: woerter.slice(0, -1).join(" "), nachname: woerter[woerter.length - 1] ?? "" };
}

/** Die Anschrift einer Person; null, wenn keines der sieben Felder gesetzt ist (wie `anschrift_der_person`). */
export function anschriftDerPerson(p: Person | null | undefined): Anschrift | null {
  if (!p) return null;
  const a = vollstaendig(p);
  return anschriftLeer(a) ? null : a;
}

function name(a: Anschrift): string {
  return `${a.vorname} ${a.nachname}`.trim();
}

function zeilen(text: string, hoechstens: number): string[] {
  return text
    .split(/\r\n|\r|\n|\u2028|\u2029/)
    .map((z) => z.trim())
    .filter(Boolean)
    .slice(0, hoechstens);
}

/** Der Absender als eine Zeile: Name, Strasse, PLZ Ort aus der Anschrift, sonst die ersten drei Zeilen des Texts, mit
 *  „, “ verbunden; leer heisst „Absender fehlt“ (das zeigt das Formular). */
export function einzeilig(brief: Pick<Brief, "absender" | "absender_anschrift">): string {
  if (brief.absender_anschrift && !anschriftLeer(brief.absender_anschrift)) {
    const a = vollstaendig(brief.absender_anschrift);
    return [name(a), a.strasse, `${a.plz} ${a.ort}`.trim()].filter(Boolean).join(", ");
  }
  return zeilen(brief.absender ?? "", 3).join(", ");
}

/** Name unter dem Brief wie beim Server (`absender_name`): aus der Anschrift, sonst die erste Zeile des Absenders. */
export function absenderName(brief: Pick<Brief, "absender" | "absender_anschrift">): string {
  const a = vollstaendig(brief.absender_anschrift);
  return name(a) || (zeilen(brief.absender ?? "", 1)[0] ?? "");
}

/** Fehlt Strasse, PLZ oder Ort im Absender (Hinweis „Deine Anschrift fehlt noch“). */
export function anschriftFehlt(brief: Pick<Brief, "absender_anschrift">): boolean {
  const a = vollstaendig(brief.absender_anschrift);
  return !a.strasse || !a.plz || !a.ort;
}

// wie Python `" ".join(x.split()).casefold()`: Leerraum zusammengefasst, Gross und klein egal (ß wie ss); auch fuer die
// Listen im Blatt „Empfänger“ (W82 Web-Empfänger E1)
export const normal = (x: string) => x.trim().split(/\s+/).join(" ").toLowerCase().replace(/ß/g, "ss");

function gleich(a: string, b: string): boolean {
  return normal(a) === normal(b);
}

/** Regel 2.4 wie der Satz (`_schluss`): die letzte nicht leere Zeile ist der Name und davor steht noch eine nicht leere
 *  Zeile (die Grussformel). Nur dann bekommt das PDF Platz fuer die Unterschrift. */
export function endetMitName(text: string, name: string): boolean {
  const voll = text.split(/\r\n|\n/).filter((z) => z.trim());
  const letzte = voll.at(-1);
  return voll.length >= 2 && name.trim() !== "" && letzte !== undefined && gleich(letzte, name);
}

/** W73e (S3, D-380 HB7, Entscheid E8): was vom Namen des Absenders am Ende des Texts fehlt. */
export type NamensLuecke = "vorname" | "nachname" | "name";
export const NAME_HINWEIS: Record<NamensLuecke, string> = {
  vorname: "Dein Vorname fehlt am Ende.",
  nachname: "Dein Nachname fehlt am Ende.",
  name: "Dein Name fehlt am Ende.",
};

/** null (kein Hinweis) bei leerem Namen oder Text, einem Platzhalter in der letzten Zeile (dann fuehrt der Chip weiter), mit
 *  Platz fuer die Unterschrift (`endetMitName`) oder wenn die einzige Zeile der Name ist. Sonst nach den Woertern der letzten
 *  Zeile (verglichen wie `gleich`): ein echtes Anfangsstueck des Namens heisst, der Nachname fehlt; ein echtes Endstueck, der
 *  Vorname; alles andere, der Name. */
export function nameAmEnde(text: string, name: string): NamensLuecke | null {
  const voll = text.split(/\r\n|\n/).filter((z) => z.trim());
  const letzte = voll.at(-1);
  if (!name.trim() || letzte === undefined || (letzte.match(PLATZHALTER) ?? []).length > 0) return null;
  if (endetMitName(text, name) || (voll.length === 1 && gleich(letzte, name))) return null;
  const n = normal(name).split(" ");
  const l = normal(letzte).split(" ");
  if (l.length < n.length && l.every((w, i) => w === n[i])) return "nachname";
  if (l.length < n.length && l.every((w, i) => w === n[n.length - l.length + i])) return "vorname";
  return "name";
}

/** `mailto:` nach RFC 6068: An, Betreff und Text mit `encodeURIComponent`, Zeilenwechsel als `%0D%0A`. Ohne Adresse
 *  oeffnet das Mailprogramm ohne Empfaenger. */
export function mailtoLink(an: string, betreff: string, text: string): string {
  const kodiert = (x: string) => encodeURIComponent(x.replace(/\r\n|\r|\n/g, "\r\n"));
  return `mailto:${kodiert(an.trim())}?subject=${kodiert(betreff)}&body=${kodiert(text)}`;
}

/** Text der Mail zum PDF (Kapitel 6). */
export function mailText(name: string): string {
  return `Sehr geehrte Damen und Herren,\n\nanbei sende ich Ihnen mein Schreiben als PDF.\n\nMit freundlichen Grüßen\n${name}`.trimEnd();
}

/** Name der PDF-Datei wie beim Versand (`worker/einstellungen.py`): nicht druckbare Zeichen werden Leerzeichen,
 *  hoechstens 60 Zeichen, „/“ und „\\“ werden „-“; ohne Betreff „Brief“. */
export function dateiname(betreff: string): string {
  const druckbar = [...betreff].map((z) => (z !== " " && /[\p{C}\p{Z}]/u.test(z) ? " " : z)).join("");
  const kurz = [...druckbar.trim()].slice(0, 60).join("") || "Brief";
  return `${kurz}.pdf`.replace(/[/\\]/g, "-");
}

// auch fuer die Felder des Empfaengers (W82 Web-Empfänger E1)
export const KLAMMER = /[[\]]/;
export const UMBRUCH = /[\p{Cc}\u2028\u2029]/u;
export const PLZ = /^[0-9A-Za-z -]*$/;
const TELEFON = /^[0-9+()/ .-]*$/;
const MAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Eingabepruefung wie der Server (`Anschrift`): je Feld ein Satz, leer heisst in Ordnung. */
export function anschriftFehler(a: Anschrift): Partial<Record<AnschriftFeld | "name", string>> {
  const aus: Partial<Record<AnschriftFeld | "name", string>> = {};
  for (const f of ANSCHRIFT_FELDER) {
    const wert = a[f];
    if (UMBRUCH.test(wert) || KLAMMER.test(wert)) aus[f] = "Ohne Zeilenumbruch und ohne eckige Klammern.";
    else if ([...wert.trim()].length > LAENGE[f]) aus[f] = `Höchstens ${LAENGE[f]} Zeichen.`;
  }
  if (!aus.plz && !PLZ.test(a.plz.trim())) aus.plz = "Nur Ziffern, Buchstaben, Leerzeichen und Bindestrich.";
  if (!aus.telefon && !TELEFON.test(a.telefon.trim())) aus.telefon = "Nur Ziffern, Leerzeichen und + ( ) / . -";
  if (!aus.email && a.email.trim() && !MAIL.test(a.email.trim())) aus.email = "Keine gültige E-Mail-Adresse.";
  if (!anschriftLeer(a) && !a.vorname.trim() && !a.nachname.trim()) aus.name = "Bitte einen Namen angeben.";
  return aus;
}

/** Der Text des Servers zu einer Ablehnung, auch bei 422 aus der Eingabepruefung (Liste mit `msg` und `loc`): je Feld
 *  der Satz ohne „Value error, “, sonst unter `gesamt`. */
export function serverFehler(fehler: unknown): { gesamt?: string; felder: Partial<Record<AnschriftFeld, string>> } {
  // V75w R1 (A2): ohne Netz wirft fetch einen TypeError mit Browsertext („Failed to fetch“, „Load failed“)
  if (!(fehler instanceof ApiFehler)) return { gesamt: fehlerText(fehler), felder: {} };
  const detail = (fehler.daten as { detail?: unknown } | null)?.detail;
  if (!Array.isArray(detail)) return { gesamt: fehler.message, felder: {} };
  const felder: Partial<Record<AnschriftFeld, string>> = {};
  const rest: string[] = [];
  for (const eintrag of detail as { msg?: unknown; loc?: unknown }[]) {
    const text = String(eintrag.msg ?? "").replace(/^Value error, /, "");
    const ort = Array.isArray(eintrag.loc) ? eintrag.loc.map(String) : [];
    const feld = ANSCHRIFT_FELDER.find((f) => ort.includes(f));
    if (feld && !felder[feld]) felder[feld] = text;
    else if (text) rest.push(text);
  }
  return { gesamt: rest.length ? [...new Set(rest)].join(" ") : undefined, felder };
}
